/**
 * Discovery Executor
 *
 * Handles the 'discovery-roi' node execution.
 * Calls the ModernizeIT API Layer /discovery/analyze endpoint.
 *
 * API Flow:
 * 1. POST /discovery/analyze → returns job_id and results
 * 2. Poll GET /jobs/{job_id} → wait for SUCCEEDED status (worker mode)
 * 3. GET /discovery/results/{job_id} → retrieve full results from S3
 *
 * This node provides executive-level analysis:
 * - Integration detection (CICS, DB2, MQ, VSAM, etc.)
 * - Business process extraction
 * - ROI calculation with real industry formulas
 * - Migration roadmap generation
 *
 * Note: This API uses Form data (not JSON body)
 *
 * Artifacts produced:
 * - discovery_summary.json
 * - integration_points.json
 * - business_processes.json
 * - api_patterns.json
 * - roi_analysis.json
 * - migration_roadmap.json
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the discovery node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing discovery analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Build the form data (Discovery API uses multipart form)
  const formData = new FormData()
  formData.append('scout_account_id', context.scout_account_id)
  formData.append('application_name', context.application_name)
  formData.append('user_id', context.user_id) // REQUIRED 2026-01-14: For AWS credentials lookup
  formData.append('enable_ai', config.enable_ai !== false ? 'true' : 'false')

  // Source path or ingest job reference (required by API)
  // The API will auto-find source files based on scout_account_id/application_name
  if (config.source_path) {
    formData.append('source_path', config.source_path)
  } else if (context.jobs?.ingest_job_id) {
    formData.append('ingest_job_id', context.jobs.ingest_job_id)
  } else if (config.ingest_job_id) {
    formData.append('ingest_job_id', config.ingest_job_id)
  } else {
    // Pass a placeholder - API will use account/app to find files
    formData.append('ingest_job_id', 'auto')
  }

  // Optional customer inputs for ROI calculation
  if (config.current_mips) {
    formData.append('current_mips', String(config.current_mips))
  }
  if (config.annual_mainframe_cost) {
    formData.append('annual_mainframe_cost', String(config.annual_mainframe_cost))
  }
  if (config.cobol_developer_count) {
    formData.append('cobol_developer_count', String(config.cobol_developer_count))
  }
  if (config.cobol_developer_salary) {
    formData.append('cobol_developer_salary', String(config.cobol_developer_salary))
  }

  onProgress(5, 'Submitting discovery analysis job...')

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/discovery/analyze`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Discovery analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Discovery analysis complete')
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, 'Processing discovery analysis...')
    await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-85%
        const mappedProgress = 15 + (progress * 0.70)
        onProgress(mappedProgress, message)
      },
      signal
    })
  }

  // 5. Fetch full results from S3-backed results endpoint
  onProgress(85, 'Retrieving discovery results...')

  let resultsData
  try {
    const resultsResponse = await fetch(`${apiBaseUrl}/discovery/results/${jobId}`, {
      headers: getAuthHeaders(),
      signal,
    })

    if (!resultsResponse.ok) {
      const errorText = await resultsResponse.text()
      throw new Error(`Failed to get results: ${resultsResponse.status} - ${errorText}`)
    }

    resultsData = await resultsResponse.json()
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    // Use job response if we can't get detailed results
    if (jobResponse) {
      resultsData = jobResponse
    } else {
      throw new Error(`Failed to retrieve results: ${error.message}`)
    }
  }

  onProgress(100, 'Discovery analysis complete')

  // 6. Return formatted result
  return formatResult(jobId, { ...jobResponse, ...resultsData })
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data) {
  const summary = data.summary || {}

  return {
    // Job tracking
    jobs: {
      discovery_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Paths
    source_path: data.source_path,
    artifacts_path: data.artifacts_path,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Summary statistics (executive-level)
    summary: {
      executive_summary: summary.executive_summary || {},
      integration_points: summary.integration_points || {},
      business_processes: summary.business_processes || {},
      api_patterns: summary.api_patterns || {},
      roi_analysis: summary.roi_analysis || {},
      migration_roadmap: summary.migration_roadmap || {},
    },

    // Artifacts produced
    artifacts: {
      discovery_summary: data.artifacts?.discovery_summary,
      integration_points: data.artifacts?.integration_points,
      business_processes: data.artifacts?.business_processes,
      api_patterns: data.artifacts?.api_patterns,
      roi_analysis: data.artifacts?.roi_analysis,
      migration_roadmap: data.artifacts?.migration_roadmap,
    },

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
